import React, { useRef, useState, useEffect, useCallback, KeyboardEvent } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './TagInput.css';

interface TagInputProps {
    values: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    label?: string;
}

const MAX_VISIBLE_TAGS = 2;

const TagInput: React.FC<TagInputProps> = ({
    values,
    onChange,
    placeholder = 'Digite e pressione Enter ou vírgula',
    disabled = false,
    label,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const inputRef = useRef<HTMLInputElement>(null);
    const fieldRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const updateDropdownPosition = useCallback(() => {
        if (fieldRef.current) {
            const rect = fieldRef.current.getBoundingClientRect();
            const isMobile = window.innerWidth <= 768;
            let leftPosition = rect.left;
            if (leftPosition + rect.width > window.innerWidth) {
                leftPosition = Math.max(16, window.innerWidth - rect.width - 16);
            }
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: leftPosition,
                width: rect.width,
                maxWidth: isMobile ? 'calc(100vw - 2rem)' : 'none',
                zIndex: 2000,
            });
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                fieldRef.current && !fieldRef.current.contains(target) &&
                dropdownRef.current && !dropdownRef.current.contains(target)
            ) {
                setDropdownOpen(false);
            }
        };
        const handleResize = () => { if (dropdownOpen) updateDropdownPosition(); };
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
        };
    }, [dropdownOpen, updateDropdownPosition]);

    const addTags = (raw: string) => {
        const newTags = raw
            .split(',')
            .map(t => t.trim())
            .filter(t => t.length > 0 && !values.includes(t));
        if (newTags.length > 0) onChange([...values, ...newTags]);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTags(inputValue);
            setInputValue('');
        } else if (e.key === 'Backspace' && inputValue === '' && values.length > 0) {
            onChange(values.slice(0, -1));
        }
    };

    const handleBlur = () => {
        if (inputValue.trim()) {
            addTags(inputValue);
            setInputValue('');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val.includes(',')) {
            addTags(val);
            setInputValue('');
        } else {
            setInputValue(val);
        }
    };

    const removeTag = (e: React.MouseEvent, tag: string) => {
        e.stopPropagation();
        onChange(values.filter(v => v !== tag));
    };

    const openDropdown = (e: React.MouseEvent) => {
        if (disabled || values.length === 0) return;
        e.stopPropagation();
        updateDropdownPosition();
        setDropdownOpen(prev => !prev);
    };

    const visibleTags = values.slice(0, MAX_VISIBLE_TAGS);
    const hiddenCount = values.length - MAX_VISIBLE_TAGS;

    const dropdown = dropdownOpen && values.length > 0 ? (
        <div className="taginput-dropdown" style={dropdownStyle} ref={dropdownRef}>
            <div className="taginput-dropdown-header">Contratos selecionados</div>
            <div className="taginput-dropdown-list">
                {values.map(tag => (
                    <div key={tag} className="taginput-dropdown-item">
                        <span className="taginput-dropdown-item-label" title={tag}>{tag}</span>
                        <button
                            className="taginput-tag-remove"
                            onClick={(e) => removeTag(e, tag)}
                            aria-label={`Remover ${tag}`}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    ) : null;

    return (
        <div className="input-container">
            {label && <label className="input-label">{label}</label>}
            <div
                ref={fieldRef}
                className={`taginput-field ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && inputRef.current?.focus()}
            >
                <div className="taginput-tags">
                    {visibleTags.map(tag => (
                        <span key={tag} className="taginput-tag" title={tag}>
                            <span className="taginput-tag-label">{tag}</span>
                            {!disabled && (
                                <button
                                    className="taginput-tag-remove"
                                    onClick={(e) => removeTag(e, tag)}
                                    tabIndex={-1}
                                    aria-label={`Remover ${tag}`}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </span>
                    ))}
                    {hiddenCount > 0 && (
                        <span
                            className="taginput-more-badge"
                            title="Clique para ver e remover contratos"
                            onClick={openDropdown}
                        >
                            +{hiddenCount}
                        </span>
                    )}
                </div>
                <input
                    ref={inputRef}
                    className="taginput-inner"
                    value={inputValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder={values.length === 0 ? placeholder : ''}
                    disabled={disabled}
                />
                {values.length > 0 && !disabled && (
                    <button
                        className="taginput-clear"
                        onClick={(e) => { e.stopPropagation(); onChange([]); setDropdownOpen(false); }}
                        tabIndex={-1}
                        aria-label="Limpar todos"
                        title="Limpar todos"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`taginput-chevron ${dropdownOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                    onClick={(e) => { e.stopPropagation(); if (!disabled && values.length > 0) { updateDropdownPosition(); setDropdownOpen(prev => !prev); } }}
                />
            </div>
            {dropdown && ReactDOM.createPortal(dropdown, document.body)}
        </div>
    );
};

export default TagInput;
